import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useCookies } from 'react-cookie';
import { 
    Flex, HStack, Button, ButtonGroup, Text, Icon, Center, Select,
    Table, Thead, Tbody, Tfoot, Tr, Th, Td, TableCaption, TableContainer,
    Badge
} from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';
import { BsArrowLeftCircle, BsArrowRightCircle } from 'react-icons/bs';
import { withNamespaces } from 'react-i18next';

import { FormattedDate, FormattedTime, FormattedNumber } from 'react-intl';
import "../../../styles/index.scss";

import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.module.css';

import { apiLoadListPayment } from '../../../redux/apis/PaymentApis';
import { getPaymentList, getPaymentTotalCount, getPaymentOffset } from '../../../redux/selectors';
import { 
    PAYMENT_TYPES, PAYMENT_STATUS, PAID_STATUS, FAILED_STATUS, PENDING_STATUS, 
    REGISTER_COMPANY, EXTEND_COMPANY, MAKE_DOCTOR_APPOINTMENT 
} from '../../../constants';
import NoDataPage from '../../../components/NoData';

function PaymentLogs({ paymentList, totalCount, offset, apiLoadListPayment, t }) {
    const [ cookies ] = useCookies(["loginCookie"]);
    const [ fromDate, setFromDate ] = useState(new Date());
    const [ toDate, setToDate ] = useState(new Date());
    const [ searchParams, setSearchParams ] = useState({ });
    const [ isSearchingDate, setIsSearchingDate ] = useState(false);
    const mounted = useRef();

    useEffect(() => {
        if(!mounted.current) {
            apiLoadListPayment({ 
                company_id: cookies?.loginCookie?.data?.company_id,
            });
            mounted.current = true;
        }
    });

    useEffect(() => {
        setIsSearchingDate(false);
    }, [paymentList]);
    
    return (
        <React.Fragment>
            <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} borderBottomWidth="2px" 
                px={4} py={2} gap={2} align="center" justify="space-between"
            >
                <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} gap={2} align="center" justify="flex-start">
                    <Text>{t("From_Date")}</Text>
                    <DatePicker dateFormat="dd/MM/yyyy" 
                        selected={fromDate}
                        onChange={
                            (date) => setFromDate(date)
                        }
                        showIcon
                        placeholderText="Từ ngày"
                        className='date-picker'
                    />

                    <Text>{t("To_Date")}</Text>
                    <DatePicker dateFormat="dd/MM/yyyy" 
                        selected={toDate}
                        onChange={
                            (date) => setToDate(date)
                        }
                        showIcon
                        placeholderText="Đến ngày"
                        className='date-picker'
                    />

                    <Button colorScheme='purple'
                        isLoading={isSearchingDate}
                        loadingText={t("Searching")}
                        onClick={() => {
                            setIsSearchingDate(true);
                            setSearchParams({ ...searchParams, fromDate, toDate });
                            setTimeout(() => {
                                apiLoadListPayment({ 
                                    company_id: cookies?.loginCookie?.data?.company_id,
                                    ...searchParams,
                                    fromDate,
                                    toDate
                                });
                            }, 500);
                        }
                    }>
                        {t("Search_By_Date")}
                    </Button>
                </Flex>
            </Flex>

            <PaginatedItems items={paymentList} totalCount={totalCount} itemOffset={offset} searchParams={searchParams} t={t}
                apiLoadListPayment={apiLoadListPayment}
            />

            {
                !(paymentList && paymentList.length > 0) && <NoDataPage text="Hiện tại không có dữ liệu"/>
            }
        </React.Fragment>
    )
}

function PaginatedItems({ items, totalCount, itemOffset, searchParams, apiLoadListPayment, t }) {
    const [ cookies ] = useCookies(["loginCookie"]);

    const handlePageClick = (event) => {
        const newOffset = (event.selected * itemsPerPage);
        
        apiLoadListPayment({ 
            company_id: cookies?.loginCookie?.data?.company_id, 
            fromDate: searchParams.fromDate, 
            toDate: searchParams.toDate,
            limit: itemsPerPage, 
            offset: newOffset 
        });
    } 

    const itemsPerPage = 10;
    const pageCount =  Math.ceil(totalCount / itemsPerPage);

    return (
        <>
            <TableContainer width="100%" px="20px" py="5px">
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr bg="#EAF0F0">
                            <Th>{t("Index")}</Th>
                            <Th>{t("Type")}</Th>
                            <Th>{t("Code")}</Th>
                            <Th>{t("Description")}</Th>
                            <Th>{t("Total_Money")}</Th>
                            <Th>{t("Status")}</Th>
                            <Th>{t("Detail")}</Th>
                            <Th>{t("Created_At")}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {
                            items && items.map((item, idx) => {
                                let paymentStatusBadge = null;

                                if(item.status === PAID_STATUS) {
                                    paymentStatusBadge = <Badge colorScheme="green">{PAYMENT_STATUS[item.status]}</Badge>
                                } else if(item.status === FAILED_STATUS) {
                                    paymentStatusBadge = <Badge colorScheme='red'>{PAYMENT_STATUS[item.status]}</Badge>
                                } else if(item.status === PENDING_STATUS) {
                                    paymentStatusBadge = <Badge>{PAYMENT_STATUS[item.status]}</Badge>
                                }

                                let extraDataText = "";
                                if(item.payment_type === MAKE_DOCTOR_APPOINTMENT) {
                                    const { name, yearOfBirth, gender, phone, address } = item.extra_data?.["patientDetail"] || {};
                                    const scheduleNumber = item.extra_data?.["scheduleNumber"] 
                                    extraDataText = `Mã khám: ${scheduleNumber}, Tên: ${name}, Giới tính: ${gender}, Năm sinh: ${yearOfBirth}, SDT: ${phone}, Địa chỉ: ${address}`;
                                }

                                return (
                                    <Tr key={item.id}>
                                        <Td>
                                            <Text fontWeight="600">{itemOffset + idx + 1}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{ item.payment_type && PAYMENT_TYPES[item.payment_type] }</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.order_code}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.description}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">
                                                { item.amount && <FormattedNumber value={item.amount}></FormattedNumber> } VND
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{paymentStatusBadge}</Text>
                                        </Td>
                                        <Td>
                                            <Text maxW="50vw" fontWeight="600" whiteSpace="initial">
                                                {extraDataText}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">
                                                <FormattedDate value={item.created_at} /> <FormattedTime value={item.created_at} />
                                            </Text>
                                        </Td>
                                    </Tr>
                                )
                            })
                        }
                    </Tbody>
                </Table>
            </TableContainer>

            {/* <Flex w="100%" justify="center" my="10px" position={{base: "relative", md: "fixed", lg: "fixed"}} bottom="0px"> */}
                <ReactPaginate 
                    breakLabel="..."
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={2}
                    pageCount={pageCount}
                    previousLabel={
                        <Center>
                            <Icon boxSize={8} as={BsArrowLeftCircle} />
                        </Center>
                    }
                    nextLabel={
                        <Center>
                            <Icon boxSize={8} as={BsArrowRightCircle} />
                        </Center>
                    }
                    renderOnZeroPageCount={null}
                    containerClassName='pagination'
                    pageClassName='page-item'
                    activeClassName='active'
                    previousClassName='previous-item'
                    nextClassName='next-item'
                />
            {/* </Flex> */}
        </>
    )
}

const mapStateToProps = (state) => {
    const paymentList        =   getPaymentList(state);
    const totalCount            =   getPaymentTotalCount(state);
    const offset                =   getPaymentOffset(state);

    return { paymentList, totalCount, offset };
}

const mapDispatchToProps = (dispatch) => ({
    apiLoadListPayment:         payload => dispatch(apiLoadListPayment(payload))
})

export default connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(PaymentLogs));